import { OPTIONS_PER_CONTRACT } from 'utils/shared/quantlib/utils';
import { CallPut } from 'utils/shared/trades/types';
import type { Trade } from 'utils/shared/trades/types';
import { getDirectionalFlowSign, getTradePositionSign } from 'utils/shared/trades/flowDirection';
import { normalizeCallPut } from 'utils/shared/trades/normalizeCallPut';
import { getTradeExecutionMs } from 'utils/shared/trades/getOptionFlowTimestampMs';

export type BucketSize = 1 | 5 | 15 | 30 | 60;

export type BucketUnit = 'm' | 'h' | 'd';

export interface BucketInterval {
  unit: BucketUnit;
  size: number;
}

export type BucketSizeValue = BucketSize | BucketInterval;

export type FlowMetric =
  | 'premium'
  | 'volume'
  | 'avgIV'
  | 'deltaExposure'
  | 'gammaExposure'
  | 'vegaExposure'
  | 'thetaExposure';

const PERCENT_MOVE = 0.01;

const SIGNED_FLOW_METRICS: ReadonlySet<FlowMetric> = new Set([
  'deltaExposure',
  'gammaExposure',
  'vegaExposure',
  'thetaExposure',
]);

export function isSignedFlowMetric(metric: FlowMetric): boolean {
  return SIGNED_FLOW_METRICS.has(metric);
}

export interface BucketData {
  bucketLabel: string;
  // TZ-aware label for chart axis/tooltip; mirrors `bucketLabel` when no `displayTimezone` is passed.
  displayLabel: string;
  callValue: number;
  putValue: number;
  netValue: number;
  price: number;
  tradeCount: number;
}

export interface BucketedFlowResult {
  buckets: BucketData[];
  totals: {
    callPremium: number;
    putPremium: number;
    netPremium: number;
    totalTrades: number;
  };
}

const ET_TIMEZONE = 'America/New_York';
const MARKET_OPEN_MIN = 9 * 60 + 30; // 9:30 AM ET = 570
const MARKET_CLOSE_MIN = 16 * 60; // 4:00 PM ET = 960

export function getTradeTimestampMs(trade: Trade): number {
  return getTradeExecutionMs(trade) ?? 0;
}

function getEtMinuteOfDay(utcMs: number): number {
  const etString = new Date(utcMs).toLocaleString('en-US', {
    timeZone: ET_TIMEZONE,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const [h, m] = etString.split(':').map(Number);
  return h * 60 + m;
}

const ET_DATE_PARTS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
});

function getEtDateAndMinute(utcMs: number): { dateKey: string; minute: number } | null {
  // Intl.DateTimeFormat.formatToParts throws RangeError on NaN — guard upstream
  // so a single malformed timestamp doesn't crash the whole chart.
  if (!Number.isFinite(utcMs)) return null;
  const parts = ET_DATE_PARTS_FMT.formatToParts(utcMs);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '0';
  const year = get('year');
  const month = get('month');
  const day = get('day');
  // hour12: false produces hours 00..23; some locales return "24" at midnight — normalize
  const hour = Number(get('hour')) % 24;
  const minute = Number(get('minute'));
  return { dateKey: `${year}-${month}-${day}`, minute: hour * 60 + minute };
}

function minuteOfDayToLabel(minuteOfDay: number): string {
  const h = Math.floor(minuteOfDay / 60);
  const m = minuteOfDay % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// Cache formatters per TZ — construction is the expensive part, `.format()` is cheap.
const DISPLAY_TIME_FMT_CACHE = new Map<string, Intl.DateTimeFormat>();

function getDisplayTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = DISPLAY_TIME_FMT_CACHE.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    DISPLAY_TIME_FMT_CACHE.set(timeZone, fmt);
  }
  return fmt;
}

// Relabels an ET trading-session minute (e.g. 570 = 9:30 ET) into the user's TZ.
function bucketMinuteToDisplayLabel(
  dateKey: string,
  etMinuteOfDay: number,
  displayTimezone: string,
): string {
  const [y, mo, d] = dateKey.split('-').map(Number);
  const h = Math.floor(etMinuteOfDay / 60);
  const m = etMinuteOfDay % 60;
  // Build naive UTC for ET h:m, then correct by the actual ET offset for that instant (DST-safe).
  const naiveUtc = Date.UTC(y, mo - 1, d, h, m, 0);
  const etParts = ET_DATE_PARTS_FMT.formatToParts(naiveUtc);
  const etGet = (type: string) => Number(etParts.find((p) => p.type === type)?.value || '0');
  const etH = etGet('hour') % 24;
  const etM = etGet('minute');
  const offsetMinutes = (h - etH) * 60 + (m - etM);
  const realUtc = naiveUtc + offsetMinutes * 60_000;

  return getDisplayTimeFormatter(displayTimezone).format(realUtc);
}

function getContractCount(trade: Trade): number {
  return Number(trade.size) || 0;
}

function getSpot(trade: Trade): number {
  return Number(trade.spot) || 0;
}

function dateKeyToShortLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  // Anchor at noon UTC so month/day round-trips regardless of local timezone
  const ms = Date.UTC(y, m - 1, d, 12, 0, 0);
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function isWeekendDateKey(dateKey: string): boolean {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
  return dow === 0 || dow === 6;
}

export function toInterval(value: BucketSizeValue): BucketInterval {
  if (typeof value === 'number') return { unit: 'm', size: value };
  return value;
}

function generateSingleDayBuckets(bucketSizeMinutes: number) {
  const buckets: { label: string; startMin: number; endMin: number }[] = [];
  let current = MARKET_OPEN_MIN;

  while (current < MARKET_CLOSE_MIN) {
    const next = Math.min(current + bucketSizeMinutes, MARKET_CLOSE_MIN);
    buckets.push({
      label: minuteOfDayToLabel(current),
      startMin: current,
      endMin: next,
    });
    current = next;
  }

  return buckets;
}

function generateMultiDayBuckets(
  dateKeys: string[],
  interval: BucketInterval,
): { label: string; dateKey: string; startMin: number; endMin: number }[] {
  const sorted = [...dateKeys].sort().filter((k) => !isWeekendDateKey(k));
  const out: { label: string; dateKey: string; startMin: number; endMin: number }[] = [];

  for (const dateKey of sorted) {
    if (interval.unit === 'd') {
      out.push({
        label: dateKeyToShortLabel(dateKey),
        dateKey,
        startMin: MARKET_OPEN_MIN,
        endMin: MARKET_CLOSE_MIN,
      });
      continue;
    }

    const stepMin = interval.unit === 'h' ? interval.size * 60 : interval.size;
    let cur = MARKET_OPEN_MIN;
    const dayLabel = dateKeyToShortLabel(dateKey);
    while (cur < MARKET_CLOSE_MIN) {
      const next = Math.min(cur + stepMin, MARKET_CLOSE_MIN);
      out.push({
        label: `${dayLabel} · ${minuteOfDayToLabel(cur)}`,
        dateKey,
        startMin: cur,
        endMin: next,
      });
      cur = next;
    }
  }

  return out;
}

function aggregateMetric(trades: Trade[], metric: FlowMetric): number {
  if (trades.length === 0) return 0;

  switch (metric) {
    case 'premium':
      return trades.reduce((sum, t) => sum + (Number(t.premium) || 0), 0);

    case 'volume':
      return trades.reduce((sum, t) => sum + (Number(t.size) || 0), 0);

    case 'avgIV': {
      let weightedSum = 0;
      let totalWeight = 0;
      for (const t of trades) {
        const iv = Number(t.impliedVol) || 0;
        const prem = Number(t.premium) || 0;
        if (prem > 0) {
          weightedSum += iv * prem;
          totalWeight += prem;
        }
      }
      return totalWeight > 0 ? (weightedSum / totalWeight) * 100 : 0;
    }

    case 'deltaExposure':
      return trades.reduce((sum, t) => {
        const positionSign = getTradePositionSign(t);
        return (
          sum +
          (Number(t.delta) || 0) *
            getContractCount(t) *
            OPTIONS_PER_CONTRACT *
            getSpot(t) *
            positionSign
        );
      }, 0);

    case 'gammaExposure':
      return trades.reduce((sum, t) => {
        const spot = getSpot(t);
        const positionSign = getTradePositionSign(t);
        return (
          sum +
          (Number(t.gamma) || 0) *
            getContractCount(t) *
            OPTIONS_PER_CONTRACT *
            spot *
            spot *
            PERCENT_MOVE *
            positionSign
        );
      }, 0);

    case 'vegaExposure':
      return trades.reduce((sum, t) => {
        return (
          sum +
          (Number(t.vega) || 0) *
            getContractCount(t) *
            OPTIONS_PER_CONTRACT *
            getTradePositionSign(t)
        );
      }, 0);

    case 'thetaExposure':
      return trades.reduce((sum, t) => {
        return (
          sum +
          (Number(t.theta) || 0) *
            getContractCount(t) *
            OPTIONS_PER_CONTRACT *
            getTradePositionSign(t)
        );
      }, 0);

    default:
      return 0;
  }
}

function aggregateDirectionalMetric(trades: Trade[], metric: FlowMetric): number {
  switch (metric) {
    case 'premium':
      return trades.reduce(
        (sum, t) => sum + (Number(t.premium) || 0) * getDirectionalFlowSign(t),
        0,
      );

    case 'volume':
      return trades.reduce((sum, t) => sum + (Number(t.size) || 0) * getDirectionalFlowSign(t), 0);

    default:
      return 0;
  }
}

function getBucketNetValue(
  entry: { calls: Trade[]; puts: Trade[] },
  metric: FlowMetric,
  callValue: number,
  putValue: number,
): number {
  if (isSignedFlowMetric(metric)) return callValue + putValue;
  if (metric === 'premium' || metric === 'volume') {
    return aggregateDirectionalMetric([...entry.calls, ...entry.puts], metric);
  }
  return callValue - Math.abs(putValue);
}

export function getTradeDateCount(trades: Trade[] | undefined, ticker?: string): number {
  if (!trades || trades.length === 0) return 0;
  const filtered = ticker ? trades.filter((t) => t.ticker === ticker) : trades;
  const dates = new Set<string>();
  for (const t of filtered) {
    const ts = getTradeTimestampMs(t);
    if (ts === 0) continue;
    const parts = getEtDateAndMinute(ts);
    if (!parts) continue;
    dates.add(parts.dateKey);
  }
  return dates.size;
}

const EMPTY_TOTALS = { callPremium: 0, putPremium: 0, netPremium: 0, totalTrades: 0 };

function computeTotals(trades: Trade[]): BucketedFlowResult['totals'] {
  const calls = trades.filter((t) => normalizeCallPut(t.cp) === CallPut.Call);
  const puts = trades.filter((t) => normalizeCallPut(t.cp) === CallPut.Put);
  const callPremium = calls.reduce((sum, t) => sum + (Number(t.premium) || 0), 0);
  const putPremium = puts.reduce((sum, t) => sum + (Number(t.premium) || 0), 0);
  return {
    callPremium,
    putPremium,
    netPremium: aggregateDirectionalMetric(trades, 'premium'),
    totalTrades: trades.length,
  };
}

function trimTrailingEmpty(buckets: BucketData[]): BucketData[] {
  let lastIdx = -1;
  for (let i = buckets.length - 1; i >= 0; i--) {
    if (buckets[i].tradeCount > 0) {
      lastIdx = i;
      break;
    }
  }
  return lastIdx === -1 ? [] : buckets.slice(0, lastIdx + 1);
}

/**
 * Pure function that buckets option flow trades by time in ET.
 *
 * Single-day bucketing (when `bucketSize` is a number or `{ unit: 'm', ... }`
 * AND trades span ≤1 ET date) keeps its original output shape — server
 * callers (e.g. netFlow.getBucketed) see the same buckets/totals as before.
 *
 * Multi-day bucketing kicks in when `bucketSize` is `{ unit: 'h' | 'd', ... }`
 * or when trades span multiple ET dates: weekends are skipped and bucket
 * labels carry the date prefix.
 *
 * For premium/volume/avg IV, `putValue` is positive — chart consumers that
 * want puts below the zero line should negate after the call. Greek exposure
 * metrics are already signed by trade side and should be displayed as-is.
 */
export default function aggregateFlowBuckets(
  trades: Trade[],
  bucketSize: BucketSizeValue,
  metric: FlowMetric,
  ticker?: string,
  // IANA TZ for `displayLabel` only; bucket boundaries stay anchored to ET market hours.
  displayTimezone?: string,
): BucketedFlowResult {
  if (!trades || trades.length === 0) {
    return { buckets: [], totals: { ...EMPTY_TOTALS } };
  }

  const filtered = ticker ? trades.filter((t) => t.ticker === ticker) : trades;
  if (filtered.length === 0) {
    return { buckets: [], totals: { ...EMPTY_TOTALS } };
  }

  const interval = toInterval(bucketSize);

  const dateSet = new Set<string>();
  for (const t of filtered) {
    const ts = getTradeTimestampMs(t);
    if (ts === 0) continue;
    const parts = getEtDateAndMinute(ts);
    if (!parts) continue;
    dateSet.add(parts.dateKey);
  }

  const isSingleDay = dateSet.size <= 1 && interval.unit === 'm';
  type Entry = {
    calls: Trade[];
    puts: Trade[];
    lastSpot: number;
    lastSpotTimestamp: number;
    count: number;
  };
  const result: BucketData[] = [];
  const useSignedValues = isSignedFlowMetric(metric);

  if (isSingleDay) {
    const timeBuckets = generateSingleDayBuckets(interval.size);
    const bucketMap: Record<string, Entry> = {};
    for (const b of timeBuckets) {
      bucketMap[b.label] = {
        calls: [],
        puts: [],
        lastSpot: 0,
        lastSpotTimestamp: 0,
        count: 0,
      };
    }

    for (const trade of filtered) {
      const ts = getTradeTimestampMs(trade);
      if (ts === 0) continue;
      const etMin = getEtMinuteOfDay(ts);
      const bucket = timeBuckets.find((b) => etMin >= b.startMin && etMin < b.endMin);
      if (!bucket) continue;

      const entry = bucketMap[bucket.label];
      const cp = normalizeCallPut(trade.cp);
      if (cp === CallPut.Call) entry.calls.push(trade);
      else if (cp === CallPut.Put) entry.puts.push(trade);

      const spot = Number(trade.spot) || 0;
      if (spot > 0 && ts >= entry.lastSpotTimestamp) {
        entry.lastSpot = spot;
        entry.lastSpotTimestamp = ts;
      }
      entry.count++;
    }

    // Single-day generator works in minutes-of-day; pick the ET date from the first trade.
    let sessionDateKey: string | null = null;
    if (displayTimezone) {
      for (const trade of filtered) {
        const ts = getTradeTimestampMs(trade);
        if (ts === 0) continue;
        const parts = getEtDateAndMinute(ts);
        if (parts) {
          sessionDateKey = parts.dateKey;
          break;
        }
      }
    }

    let lastKnownPrice = 0;
    for (const b of timeBuckets) {
      const entry = bucketMap[b.label];
      const callValue = aggregateMetric(entry.calls, metric);
      const putValue = aggregateMetric(entry.puts, metric);
      if (entry.lastSpot > 0) lastKnownPrice = entry.lastSpot;
      const chartPutValue = useSignedValues ? putValue : Math.abs(putValue);
      const displayLabel =
        displayTimezone && sessionDateKey
          ? bucketMinuteToDisplayLabel(sessionDateKey, b.startMin, displayTimezone)
          : b.label;
      result.push({
        bucketLabel: b.label,
        displayLabel,
        callValue,
        putValue: chartPutValue,
        netValue: getBucketNetValue(entry, metric, callValue, putValue),
        price: entry.lastSpot || lastKnownPrice,
        tradeCount: entry.count,
      });
    }
  } else {
    const timeBuckets = generateMultiDayBuckets(Array.from(dateSet), interval);
    if (timeBuckets.length === 0) {
      return { buckets: [], totals: computeTotals(filtered) };
    }

    const bucketMap: Record<string, Entry> = {};
    for (const b of timeBuckets) {
      bucketMap[b.label] = {
        calls: [],
        puts: [],
        lastSpot: 0,
        lastSpotTimestamp: 0,
        count: 0,
      };
    }

    const bucketsByDate: Record<string, typeof timeBuckets> = {};
    for (const b of timeBuckets) {
      if (!bucketsByDate[b.dateKey]) bucketsByDate[b.dateKey] = [];
      bucketsByDate[b.dateKey].push(b);
    }

    for (const trade of filtered) {
      const ts = getTradeTimestampMs(trade);
      if (ts === 0) continue;
      const parts = getEtDateAndMinute(ts);
      if (!parts) continue;
      const { dateKey, minute } = parts;
      const dayBuckets = bucketsByDate[dateKey];
      if (!dayBuckets) continue;
      const bucket = dayBuckets.find((b) => minute >= b.startMin && minute < b.endMin);
      if (!bucket) continue;

      const entry = bucketMap[bucket.label];
      const cp = normalizeCallPut(trade.cp);
      if (cp === CallPut.Call) entry.calls.push(trade);
      else if (cp === CallPut.Put) entry.puts.push(trade);

      const spot = Number(trade.spot) || 0;
      if (spot > 0 && ts >= entry.lastSpotTimestamp) {
        entry.lastSpot = spot;
        entry.lastSpotTimestamp = ts;
      }
      entry.count++;
    }

    const isDayBucket = interval.unit === 'd';

    let lastKnownPrice = 0;
    for (const b of timeBuckets) {
      const entry = bucketMap[b.label];
      const callValue = aggregateMetric(entry.calls, metric);
      const putValue = aggregateMetric(entry.puts, metric);
      if (entry.lastSpot > 0) lastKnownPrice = entry.lastSpot;
      const chartPutValue = useSignedValues ? putValue : Math.abs(putValue);
      // Day buckets carry no intra-day time, so no TZ translation needed.
      const displayLabel =
        displayTimezone && !isDayBucket
          ? `${dateKeyToShortLabel(b.dateKey)} · ${bucketMinuteToDisplayLabel(
              b.dateKey,
              b.startMin,
              displayTimezone,
            )}`
          : b.label;
      result.push({
        bucketLabel: b.label,
        displayLabel,
        callValue,
        putValue: chartPutValue,
        netValue: getBucketNetValue(entry, metric, callValue, putValue),
        price: entry.lastSpot || lastKnownPrice,
        tradeCount: entry.count,
      });
    }
  }

  return {
    buckets: trimTrailingEmpty(result),
    totals: computeTotals(filtered),
  };
}

/**
 * Replaces leading zero `price` entries with the first non-zero price found
 * later in the series so the price line starts at a meaningful level rather
 * than at zero when early buckets contain no trades. Mutates in place.
 */
export function backfillLeadingPrices(buckets: BucketData[]): void {
  let firstPrice = 0;
  for (const r of buckets) {
    if (r.price > 0) {
      firstPrice = r.price;
      break;
    }
  }
  if (firstPrice === 0) return;
  for (let i = 0; i < buckets.length; i++) {
    if (buckets[i].price > 0) break;
    buckets[i].price = firstPrice;
  }
}
